import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Trash2, Building2, User } from 'lucide-react';
import { useAdminCache } from '../../hooks/useAdminCache';

export default function CacheManagementPage() {
  const { clearAll, clearInstitute, clearUser } = useAdminCache();

  const [flushDialogOpen, setFlushDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [instituteId, setInstituteId] = useState('');
  const [userId, setUserId] = useState('');

  const handleFlushAll = () => {
    clearAll.mutate(undefined, {
      onSuccess: () => {
        setFlushDialogOpen(false);
        setConfirmText('');
      },
    });
  };

  const handleClearInstitute = () => {
    const id = parseInt(instituteId, 10);
    if (!id) return;
    clearInstitute.mutate(id, { onSuccess: () => setInstituteId('') });
  };

  const handleClearUser = () => {
    if (!userId.trim()) return;
    clearUser.mutate(userId.trim(), { onSuccess: () => setUserId('') });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 640 }}>
      {/* Flush All */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Trash2 size={18} color="#EF4444" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Flush All Cache
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Removes every entry from the in-memory cache. All data will be re-fetched from the
          database on the next request. Use this after a data migration or when stale data is
          suspected across the application.
        </Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<Trash2 size={15} />}
          onClick={() => setFlushDialogOpen(true)}
        >
          Flush All Cache
        </Button>
      </Paper>

      <Divider />

      {/* Clear by Institute */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Building2 size={18} color="#8A70D6" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Clear Institute Cache
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Clears the active status and current academic year entries for a specific institute.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <TextField
            label="Institute ID"
            size="small"
            value={instituteId}
            onChange={(e) => setInstituteId(e.target.value)}
            sx={{ width: 180 }}
            type="number"
          />
          <Button
            variant="outlined"
            onClick={handleClearInstitute}
            disabled={!instituteId || clearInstitute.isPending}
          >
            {clearInstitute.isPending ? 'Clearing…' : 'Clear'}
          </Button>
        </Box>
      </Paper>

      <Divider />

      {/* Clear by User */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <User size={18} color="#8A70D6" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Clear User Permissions Cache
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Clears the cached permission set for a specific user by their user ID (GUID).
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <TextField
            label="User ID (GUID)"
            size="small"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            sx={{ width: 320 }}
          />
          <Button
            variant="outlined"
            onClick={handleClearUser}
            disabled={!userId.trim() || clearUser.isPending}
          >
            {clearUser.isPending ? 'Clearing…' : 'Clear'}
          </Button>
        </Box>
      </Paper>

      {/* Flush-all confirmation dialog */}
      <Dialog
        open={flushDialogOpen}
        onClose={() => { setFlushDialogOpen(false); setConfirmText(''); }}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid #EF444430' } } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: '#EF444420',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#EF4444',
                flexShrink: 0,
              }}
            >
              <Trash2 size={16} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Flush All Cache</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will clear every cached entry across the entire application. All data will be
            reloaded from the database. Type <strong>CONFIRM</strong> to proceed.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Type CONFIRM"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => { setFlushDialogOpen(false); setConfirmText(''); }}
            disabled={clearAll.isPending}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleFlushAll}
            disabled={confirmText !== 'CONFIRM' || clearAll.isPending}
            sx={{ flex: 1 }}
          >
            {clearAll.isPending ? 'Flushing…' : 'Flush All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
